import type { Vec } from '../destruction/structure';
import type { RoadEnvelope } from './traffic-space';
interface Crossing {
  a: Vec;
  b: Vec;
  requests: Map<string, { until: number; active: boolean }>;
  active: number;
  bins: string[];
}
/** Short-lived requests bind pedestrian phases to real vehicle stop lines.
 * A person already admitted retains the crossing until physically across. */
export class CrossingControl {
  readonly crossings = new Map<string, Crossing>();
  private bins = new Map<string, Set<Crossing>>();
  /** A short pedestrian admission phase is followed by clearance and traffic.
   * Already admitted walkers keep protection until physically across. */
  green(junction: number, now: number): boolean {
    return (now + (junction % 3) * 12) % 36 < 6;
  }
  request(id: string, owner: string, a: Vec, b: Vec, now: number, green: boolean, admitted: boolean): void {
    let crossing = this.crossings.get(id);
    if (!crossing) {
      crossing = { a: { ...a }, b: { ...b }, requests: new Map(), active: 0, bins: [] };
      this.crossings.set(id, crossing);
      for (let x = Math.floor(Math.min(a.x, b.x) / 24); x <= Math.floor(Math.max(a.x, b.x) / 24); x++)
        for (let z = Math.floor(Math.min(a.z, b.z) / 24); z <= Math.floor(Math.max(a.z, b.z) / 24); z++) {
          const key = `${x},${z}`,
            set = this.bins.get(key) ?? new Set<Crossing>();
          set.add(crossing);
          this.bins.set(key, set);
          crossing.bins.push(key);
        }
    }
    const prior = crossing.requests.get(owner),
      active = green || admitted;
    crossing.active += Number(active) - Number(prior?.active ?? false);
    crossing.requests.set(owner, { until: now + 0.6, active });
  }
  step(now: number): void {
    for (const [id, c] of this.crossings) {
      for (const [owner, r] of c.requests)
        if (r.until < now) {
          c.requests.delete(owner);
          c.active -= Number(r.active);
        }
      if (!c.requests.size) {
        this.crossings.delete(id);
        for (const key of c.bins) {
          const set = this.bins.get(key)!;
          set.delete(c);
          if (!set.size) this.bins.delete(key);
        }
      }
    }
  }
  clear(): void {
    this.crossings.clear();
    this.bins.clear();
  }
  speed(car: RoadEnvelope & { y: number }, direction: Pick<Vec, 'x' | 'z'>): number {
    let speed = Infinity;
    const end = { x: car.x + direction.x * 28, z: car.z + direction.z * 28 },
      candidates = new Set<Crossing>(),
      margin = car.width / 2 + 0.7;
    for (
      let x = Math.floor((Math.min(car.x, end.x) - margin) / 24);
      x <= Math.floor((Math.max(car.x, end.x) + margin) / 24);
      x++
    )
      for (
        let z = Math.floor((Math.min(car.z, end.z) - margin) / 24);
        z <= Math.floor((Math.max(car.z, end.z) + margin) / 24);
        z++
      )
        for (const crossing of this.bins.get(`${x},${z}`) ?? []) candidates.add(crossing);
    for (const c of candidates) {
      if (Math.abs(car.y - c.a.y) > 1.2 || c.active === 0) continue;
      const dx = c.b.x - c.a.x,
        dz = c.b.z - c.a.z,
        len = Math.hypot(dx, dz),
        denominator = direction.x * dz - direction.z * dx;
      if (Math.abs(denominator) < 0.01) continue;
      const ax = c.a.x - car.x,
        az = c.a.z - car.z,
        ahead = (ax * dz - az * dx) / denominator,
        along = (ax * direction.z - az * direction.x) / denominator;
      if (along < -(car.width / 2 + 0.7) / len || along > 1 + (car.width / 2 + 0.7) / len || ahead > 28)
        continue;
      // A vehicle straddling the line clears it before anybody is admitted.
      if (ahead < car.length / 2 + 1.0) continue;
      speed = Math.min(speed, Math.sqrt(6 * Math.max(0, ahead - car.length / 2 - 1.4)));
    }
    return speed;
  }
}
/** Segment against the same oriented vehicle footprint used by traffic. */
export function crossesVehicle(a: Vec, b: Vec, car: RoadEnvelope & { y: number }, margin = 0.8): boolean {
  if (Math.abs(car.y - a.y) > 1.2) return false;
  const cos = Math.cos(car.yaw),
    sin = Math.sin(car.yaw),
    local = (p: Vec) => ({
      x: (p.x - car.x) * cos - (p.z - car.z) * sin,
      z: (p.x - car.x) * sin + (p.z - car.z) * cos,
    }),
    A = local(a),
    B = local(b);
  let low = 0,
    high = 1;
  for (const [start, delta, half] of [
    [A.x, B.x - A.x, car.width / 2 + margin],
    [A.z, B.z - A.z, car.length / 2 + margin],
  ]) {
    if (Math.abs(delta) < 1e-8) {
      if (Math.abs(start) > half) return false;
      continue;
    }
    const first = (-half - start) / delta,
      second = (half - start) / delta;
    low = Math.max(low, Math.min(first, second));
    high = Math.min(high, Math.max(first, second));
    if (low > high) return false;
  }
  return true;
}
